import _isEmpty from 'lodash/isEmpty';
import { normalizeNumber } from '../utilsV3/amountUtils';
import {
  LOGO_TARGET_CAMPAIGN,
  LOGO_TARGET_PROFILE,
  LOGO_TARGET_NOLOGO,
} from '../reducers/reducerCampaigns';
import {
  getButtonImageAndUrl,
  shouldUsePayPalButtonStyle,
} from '../utilsV3/buttonImage';
import { getEndOfDayTime } from '../utilsV3/dateUtils';
import { formatNumber } from '../../utility/worldReadyUtils';

export const SERVER_CAMPAIGN = {
  ID: 'id',
  STATUS: 'status',
  CAMPAIGN_TYPE: 'campaign_type',
  EVENT_TYPE: 'event_type',
  EVENT_NAME: 'event_name',
  EVENT_TITLE: 'event_title',
  EVENT_URL: 'event_url',
  EVENT_DESCRIPTION: 'event_description',
  EVENT_DESCRIPTION_RICH_TEXT: 'rich_text_description',
  COUNTRY_CODE: 'country_code',
  CURRENCY_CODE: 'currency_code',
  CREATOR_ID: 'creator_id',
  PARTICIPANTS: 'participants',
  PARTICIPANT_TYPE: 'participant_type',
  PARTICIPANT_VALUE: 'participant_value',
  CATEGORIES: 'categories',
  POSTAL_CODE: 'postal_code',
  START_TIME: 'start_time',
  END_TIME: 'end_time',
  TEMPLATE_DATA: 'template_data',
  TRANSACTION_SUMMARY: 'transaction_summary',
  TOTAL_AMOUNT: 'total_amount',
  TOTAL_NUMBER_OF_TRANSACTIONS: 'total_number_of_transactions',
  TID: 'tid',
  IS_TARGET_REACHED: 'is_target_reached',
  DONATION_LIMIT_REACHED: 'donation_limit_reached',
  NET_AMOUNT: 'net_amount',
  URLS: 'urls',
  BANNER_URL: 'banner_url',
  LOGO_URL: 'logo_url',
  LOGO_TARGET: 'logo_target',
  NO_LOGO_URL: 'noLogoImage',
  CANCEL_URL: 'cancel_url',
  RETURN_URL: 'return_url',
  DISPLAY_PROPERTIES: 'display_properties',
  RECURRING_SELECTION: 'recurring_selection',
  ANONYMOUS_SELECTION: 'anonymous_selection',
  COVER_FEES: 'cover_fees',
  NOTE: 'note',
  SHIPPING_OPTION: 'shipping_option',
  PREVIEW_TYPE: 'preview_type',
  DONATION_UNITS: 'donation_units',
  AMOUNT: 'amount',
  CURRENCY: 'currency',
  TARGET_AMOUNT: 'target_amount',
  VALUE: 'value',
  BUTTON_IMAGE_URL: 'button_image_url',
  BUTTON_IMAGE: 'button_image',
  LANGUAGE: 'language',
  EMBED_WIDGET: 'embed_widget',
  TIME_UPDATED: 'time_updated',
  HAS_END_TIME: 'hasEndTime',
  METADATA: 'donate_button_campaign_meta_data',
  DRAFT_META: 'draft_meta',
  DRAFT_VERSION: 'draft_version',
  DRAFT_STEP: 'draft_step',
};

export const CLIENT_CAMPAIGN = {
  ID: 'id',
  STATUS: 'status',
  CAMPAIGN_TYPE: 'campaignType',
  EVENT_DESCRIPTION: 'plainTextDescription',
  EVENT_DESCRIPTION_RICH_TEXT: 'description',
  EVENT_TYPE: 'eventType',
  EVENT_NAME: 'receiverName',
  EVENT_TITLE: 'eventTitle',
  EVENT_URL: 'eventUrl',
  COUNTRY_CODE: 'countryCode',
  CURRENCY_CODE: 'currencyCode',
  CREATOR_ID: 'creatorId',
  PARTICIPANTS: 'participants',
  PARTICIPANT_TYPE: 'participantType',
  PARTICIPANT_VALUE: 'participantValue',
  CATEGORIES: 'categories',
  POSTAL_CODE: 'postalCode',
  START_TIME: 'startTime',
  END_TIME: 'endTime',
  TEMPLATE_DATA: 'templateData',
  TRANSACTION_SUMMARY: 'transactionSummary',
  TOTAL_AMOUNT: 'TotalAmount',
  TOTAL_NUMBER_OF_TRANSACTIONS: 'transactionCount',
  TID: 'tid',
  IS_TARGET_REACHED: 'targetReached',
  DONATION_LIMIT_REACHED: 'donationLimitReached',
  NET_AMOUNT: 'netAmount',
  BANNER_URL: 'bannerImageUrl',
  LOGO_URL: 'logoUrl',
  LOGO_TARGET: 'logoTarget',
  NO_LOGO_URL: 'noLogoImage',
  NO_CANCEL_RETURN: 'noCancelReturn',
  BUSINESS_PROFILE_LOGO_URL: 'businessProfileLogoUrl',
  CANCEL_URL: 'cancelReturn',
  RETURN_URL: 'return',
  RECURRING_SELECTION: 'recurringEnabled',
  ANONYMOUS_SELECTION: 'anonymousSelection',
  COVER_FEES: 'coverFeesEnabled',
  NOTE: 'note',
  SHIPPING_OPTION: 'noShipping',
  PREVIEW_TYPE: 'previewType',
  AMOUNT: 'amountValue',
  MERCHANT_AMOUNT_1: 'merchantAmount1',
  MERCHANT_AMOUNT_2: 'merchantAmount2',
  MERCHANT_AMOUNT_3: 'merchantAmount3',
  AMOUNT_TYPE: 'amountType',
  USE_PAYPAL_BUTTON_STYLE: 'usePaypalButtonStyle',
  HAS_END_TIME: 'hasEndTime',
  TARGET_AMOUNT: 'targetAmount',
  BUTTON_IMAGE_URL: 'buttonImageUrl',
  BUTTON_IMAGE: 'buttonImage',
  SHOW_ACCEPTED_CARD_LOGOS: 'showAcceptedCardLogos',
  LANGUAGE: 'language',
  BTN_SIZE: 'buttonSize',
  NO_PAYPAL_BUTTON_STYLES: 'noPaypalButtonStyles',
  BUTTON_SIZE: 'buttonSize',
  EMBED_WIDGET: 'embedWidget',
  NO_EMBED_WIDGET: 'noEmbedWidget',
  LAST_UPDATED: 'lastUpdated',
  METADATA: 'clientMetadata',
  DRAFT_META: 'draftMeta',
  DRAFT_VERSION: 'draftVersion',
  DRAFT_STEP: 'draftStep',
};

/**
 * Returns true if accepted card logos should be displayed
 */
export const shouldShowAcceptedCardLogos = (button_image_url) => {
  return !!/CC_/.test(button_image_url); // eslint-disable-line
};

/**
 * Gets button size
 * @param  {Object} button Button
 * @return {String}        Size of the button ('small' or 'large')
 */
const buttonSizeMap = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
};

/**
 * Deserializes stored data for security
 */
const unsafeFields = [
  'rich_text_description',
  'event_name',
  'event_description',
  'target_amount',
  'template_data',
];
const ESCAPED_CHARS = {
  '\\u003C': '<',
  '\\u003E': '>',
  '\\u002F': '/',
  '\\u2028': '\u2028',
  '\\u2029': '\u2029',
};
const deserializeUnsafeCharacters = function (text, rules) {
  var result = text;
  for (var x in rules) {
    result = result.replaceAll(x, rules[x]);
  }
  return result;
};
const deserializeCampaignFields = (campaign, req) => {
  try {
    for (let i = 0; i < unsafeFields.length; i++) {
      const field = unsafeFields[i];
      if (campaign[field])
        campaign[field] = deserializeUnsafeCharacters(
          campaign[field],
          ESCAPED_CHARS
        );
    }
    return campaign;
  } catch (e) {
    return campaign;
  }
};

export const getButtonSize = (button_image_url) => {
  const size = /_(\D{2})\.gif/.exec(button_image_url); // eslint-disable-line
  if (size && size.length) {
    return buttonSizeMap[size[1].toLowerCase()];
  }

  // Default to 'small' ?
  return 'small';
};

/**
 * Gets transaction summary details from transaction_summary object
 * @param  {Object} transactionSummary   transaction_summary object(server format)
 * @param  {String} property Property to get
 * @return {String}          Value of the property
 */
const getFromTransactionSummary = (transactionSummary, property) =>
  transactionSummary[property];

/**
 * Gets template_data details from template_data object
 * @param  {Object} templateData   template_data object(server format)
 * @param  {String} property Property to get
 * @return {String}          Value of the property
 */
const getFromTemplateData = (templateData, property) => {
  switch (property) {
    case SERVER_CAMPAIGN.BANNER_URL:
    case SERVER_CAMPAIGN.LOGO_URL:
    case SERVER_CAMPAIGN.CANCEL_URL:
    case SERVER_CAMPAIGN.RETURN_URL:
    case SERVER_CAMPAIGN.BUTTON_IMAGE_URL:
      const urlsObj = templateData[SERVER_CAMPAIGN.URLS];
      if (urlsObj) {
        return urlsObj[property];
      }
      break;
    case SERVER_CAMPAIGN.RECURRING_SELECTION:
    case SERVER_CAMPAIGN.ANONYMOUS_SELECTION:
    case SERVER_CAMPAIGN.LOGO_TARGET:
    case SERVER_CAMPAIGN.COVER_FEES:
    case SERVER_CAMPAIGN.NOTE:
    case SERVER_CAMPAIGN.SHIPPING_OPTION:
    case SERVER_CAMPAIGN.PREVIEW_TYPE:
    case SERVER_CAMPAIGN.LANGUAGE:
    case SERVER_CAMPAIGN.EMBED_WIDGET:
      const displayPropObj = templateData[SERVER_CAMPAIGN.DISPLAY_PROPERTIES];
      if (displayPropObj) {
        return displayPropObj[property];
      }
      break;
    case SERVER_CAMPAIGN.AMOUNT:
      const donationUnitsObj = templateData[SERVER_CAMPAIGN.DONATION_UNITS];
      if (donationUnitsObj) {
        return donationUnitsObj[property].map((amountObj) => {
          if (amountObj.value === '') {
            return { ...amountObj, value: 0 };
          }
          return amountObj;
        });
      }
      break;
    case SERVER_CAMPAIGN.DRAFT_VERSION:
    case SERVER_CAMPAIGN.DRAFT_STEP:
      const draftMetaObj = templateData[SERVER_CAMPAIGN.DRAFT_META];
      if (draftMetaObj) {
        return draftMetaObj[property];
      }
      break;
    default:
      break;
  }
};

/**
 * Adds property to template_data
 * @param {Object} clientFormat Campaign object(client format)
 * @param {String} clientKey    Client property
 * @param {Object} serverFormat Campaign object(server format)
 * @param {String} serverKey    Server property
 */
const addToTemplateData = (
  clientFormat,
  clientKey,
  serverFormat,
  serverKey
) => {
  if (clientFormat[clientKey] !== undefined) {
    switch (clientKey) {
      case CLIENT_CAMPAIGN.BANNER_URL:
      case CLIENT_CAMPAIGN.LOGO_URL:
      case CLIENT_CAMPAIGN.CANCEL_URL:
      case CLIENT_CAMPAIGN.RETURN_URL:
      case CLIENT_CAMPAIGN.BUTTON_IMAGE_URL:
        serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA] = {
          ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA],
          [SERVER_CAMPAIGN.URLS]: {
            ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][
              SERVER_CAMPAIGN.URLS
            ],
            [serverKey]: clientFormat[clientKey],
          },
        };
        break;
      case CLIENT_CAMPAIGN.RECURRING_SELECTION:
        serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA] = {
          ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA],
          [SERVER_CAMPAIGN.DISPLAY_PROPERTIES]: {
            ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][
              SERVER_CAMPAIGN.DISPLAY_PROPERTIES
            ],
            [serverKey]:
              clientFormat[clientKey] === 'yes' &&
              clientFormat[CLIENT_CAMPAIGN.HAS_END_TIME] === 'no'
                ? 1
                : 0,
          },
        };
        break;
      case CLIENT_CAMPAIGN.COVER_FEES:
        serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA] = {
          ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA],
          [SERVER_CAMPAIGN.DISPLAY_PROPERTIES]: {
            ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][
              SERVER_CAMPAIGN.DISPLAY_PROPERTIES
            ],
            [serverKey]: clientFormat[clientKey] === 'yes' ? 1 : 0,
          },
        };
        break;
      case CLIENT_CAMPAIGN.ANONYMOUS_SELECTION:
      case CLIENT_CAMPAIGN.LOGO_TARGET:
      case CLIENT_CAMPAIGN.NOTE:
      case CLIENT_CAMPAIGN.PREVIEW_TYPE:
      case CLIENT_CAMPAIGN.BUTTON_IMAGE:
      case CLIENT_CAMPAIGN.LANGUAGE:
      case CLIENT_CAMPAIGN.EMBED_WIDGET:
        serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA] = {
          ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA],
          [SERVER_CAMPAIGN.DISPLAY_PROPERTIES]: {
            ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][
              SERVER_CAMPAIGN.DISPLAY_PROPERTIES
            ],
            [serverKey]: clientFormat[clientKey],
          },
        };
        break;
      case CLIENT_CAMPAIGN.SHIPPING_OPTION: {
        serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA] = {
          ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA],
          [SERVER_CAMPAIGN.DISPLAY_PROPERTIES]: {
            ...serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][
              SERVER_CAMPAIGN.DISPLAY_PROPERTIES
            ],
            [serverKey]: clientFormat[clientKey] === 'no' ? 1 : 0,
          },
        };
        break;
      }
      default:
        break;
    }
  }
};

/**
 * Adds property to template_data
 * @param {Object} serverFormatButton Campaign object(server format)
 */
const getLogoVariable = (logoTarget) => {
  let noLogoImage = 'yes';
  switch (logoTarget) {
    case LOGO_TARGET_PROFILE:
    case LOGO_TARGET_CAMPAIGN:
      noLogoImage = 'no';
      break;
    case LOGO_TARGET_NOLOGO:
      noLogoImage = 'yes';
      break;
    default:
      noLogoImage = 'yes';
      break;
  }
  return noLogoImage;
};

export const transformServerFormatToClient = (serverFormat) => {
  const clientFormat = {};
  serverFormat = deserializeCampaignFields(serverFormat);
  if (serverFormat.id) {
    clientFormat[CLIENT_CAMPAIGN.ID] = serverFormat[SERVER_CAMPAIGN.ID];
  }

  clientFormat[CLIENT_CAMPAIGN.CAMPAIGN_TYPE] =
    serverFormat[SERVER_CAMPAIGN.CAMPAIGN_TYPE];
  clientFormat[CLIENT_CAMPAIGN.STATUS] = serverFormat[SERVER_CAMPAIGN.STATUS];
  clientFormat[CLIENT_CAMPAIGN.EVENT_TYPE] =
    serverFormat[SERVER_CAMPAIGN.EVENT_TYPE];
  clientFormat[CLIENT_CAMPAIGN.EVENT_NAME] =
    serverFormat[SERVER_CAMPAIGN.EVENT_NAME];
  clientFormat[CLIENT_CAMPAIGN.EVENT_TITLE] =
    serverFormat[SERVER_CAMPAIGN.EVENT_TITLE] || '';
  clientFormat[CLIENT_CAMPAIGN.EVENT_URL] =
    serverFormat[SERVER_CAMPAIGN.EVENT_URL];

  const richTextDescription =
    serverFormat[SERVER_CAMPAIGN.EVENT_DESCRIPTION_RICH_TEXT];
  clientFormat[CLIENT_CAMPAIGN.EVENT_DESCRIPTION_RICH_TEXT] =
    JSON.parse(richTextDescription);
  clientFormat[CLIENT_CAMPAIGN.COUNTRY_CODE] =
    serverFormat[SERVER_CAMPAIGN.COUNTRY_CODE];
  clientFormat[CLIENT_CAMPAIGN.CURRENCY_CODE] =
    serverFormat[SERVER_CAMPAIGN.CURRENCY_CODE];
  clientFormat[CLIENT_CAMPAIGN.CREATOR_ID] =
    serverFormat[SERVER_CAMPAIGN.CREATOR_ID];
  clientFormat[CLIENT_CAMPAIGN.PARTICIPANTS] = [];
  clientFormat[CLIENT_CAMPAIGN.CATEGORIES] = [
    ...serverFormat[SERVER_CAMPAIGN.CATEGORIES],
  ];
  clientFormat[CLIENT_CAMPAIGN.POSTAL_CODE] =
    serverFormat[SERVER_CAMPAIGN.POSTAL_CODE];
  clientFormat[CLIENT_CAMPAIGN.START_TIME] =
    serverFormat[SERVER_CAMPAIGN.START_TIME];

  if (!_isEmpty(serverFormat[SERVER_CAMPAIGN.END_TIME])) {
    const formattedDate = new Date(
      Date.parse(serverFormat[SERVER_CAMPAIGN.END_TIME])
    );
    formattedDate.setHours(0, 0, 0, 0);
    clientFormat[CLIENT_CAMPAIGN.END_TIME] = formattedDate;
  }

  clientFormat[CLIENT_CAMPAIGN.HAS_END_TIME] = !_isEmpty(
    serverFormat[SERVER_CAMPAIGN.END_TIME]
  )
    ? 'yes'
    : 'no';

  const numericTargetAmount = Number(
    serverFormat[SERVER_CAMPAIGN.TARGET_AMOUNT] || 0
  );
  clientFormat[CLIENT_CAMPAIGN.TARGET_AMOUNT] =
    formatNumber(numericTargetAmount);

  // last update time is in unix timestamp, need to convert it to milleseconds frist
  const lastUpdatedTimestamp =
    serverFormat[SERVER_CAMPAIGN.TIME_UPDATED] * 1000;
  const lastUpdateTimeInUtc = new Date(lastUpdatedTimestamp).toUTCString();
  clientFormat[CLIENT_CAMPAIGN.LAST_UPDATED] = lastUpdateTimeInUtc;

  // transaction_summary
  clientFormat[CLIENT_CAMPAIGN.TOTAL_AMOUNT] = getFromTransactionSummary(
    serverFormat[SERVER_CAMPAIGN.TRANSACTION_SUMMARY],
    SERVER_CAMPAIGN.TOTAL_AMOUNT
  );
  clientFormat[CLIENT_CAMPAIGN.TOTAL_NUMBER_OF_TRANSACTIONS] =
    getFromTransactionSummary(
      serverFormat[SERVER_CAMPAIGN.TRANSACTION_SUMMARY],
      SERVER_CAMPAIGN.TOTAL_NUMBER_OF_TRANSACTIONS
    );
  clientFormat[CLIENT_CAMPAIGN.TID] = getFromTransactionSummary(
    serverFormat[SERVER_CAMPAIGN.TRANSACTION_SUMMARY],
    SERVER_CAMPAIGN.TID
  );
  clientFormat[CLIENT_CAMPAIGN.IS_TARGET_REACHED] = getFromTransactionSummary(
    serverFormat[SERVER_CAMPAIGN.TRANSACTION_SUMMARY],
    SERVER_CAMPAIGN.IS_TARGET_REACHED
  );
  clientFormat[CLIENT_CAMPAIGN.DONATION_LIMIT_REACHED] =
    getFromTransactionSummary(
      serverFormat[SERVER_CAMPAIGN.TRANSACTION_SUMMARY],
      SERVER_CAMPAIGN.DONATION_LIMIT_REACHED
    );
  clientFormat[CLIENT_CAMPAIGN.NET_AMOUNT] = getFromTransactionSummary(
    serverFormat[SERVER_CAMPAIGN.TRANSACTION_SUMMARY],
    SERVER_CAMPAIGN.NET_AMOUNT
  );

  // template_data

  const templateDataJson = JSON.parse(
    serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA]
  );

  if (clientFormat[CLIENT_CAMPAIGN.STATUS] === 'DRAFT') {
    clientFormat[CLIENT_CAMPAIGN.DRAFT_META] = {
      [CLIENT_CAMPAIGN.DRAFT_VERSION]: getFromTemplateData(
        templateDataJson,
        SERVER_CAMPAIGN.DRAFT_VERSION
      ),
      [CLIENT_CAMPAIGN.DRAFT_STEP]: getFromTemplateData(
        templateDataJson,
        SERVER_CAMPAIGN.DRAFT_STEP
      ),
    };
  }

  clientFormat[CLIENT_CAMPAIGN.BANNER_URL] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.BANNER_URL
  );
  clientFormat[CLIENT_CAMPAIGN.EMBED_WIDGET] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.EMBED_WIDGET
  );
  clientFormat[CLIENT_CAMPAIGN.LOGO_URL] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.LOGO_URL
  );
  clientFormat[CLIENT_CAMPAIGN.LOGO_URL] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.LOGO_URL
  );
  clientFormat[CLIENT_CAMPAIGN.CANCEL_URL] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.CANCEL_URL
  );
  clientFormat[CLIENT_CAMPAIGN.RETURN_URL] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.RETURN_URL
  );
  clientFormat[CLIENT_CAMPAIGN.BUSINESS_PROFILE_LOGO_URL] =
    serverFormat.businessProfileLogoUrl ?? '';

  clientFormat[CLIENT_CAMPAIGN.LOGO_TARGET] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.LOGO_TARGET
  );
  const noLogo = getLogoVariable(clientFormat[CLIENT_CAMPAIGN.LOGO_TARGET]);
  clientFormat[CLIENT_CAMPAIGN.NO_LOGO_URL] = noLogo;
  clientFormat[CLIENT_CAMPAIGN.RECURRING_SELECTION] =
    parseInt(
      getFromTemplateData(
        templateDataJson,
        SERVER_CAMPAIGN.RECURRING_SELECTION
      ),
      10
    ) === 0
      ? 'no'
      : 'yes';
  clientFormat[CLIENT_CAMPAIGN.ANONYMOUS_SELECTION] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.ANONYMOUS_SELECTION
  );
  clientFormat[CLIENT_CAMPAIGN.COVER_FEES] =
    parseInt(
      getFromTemplateData(templateDataJson, SERVER_CAMPAIGN.COVER_FEES),
      10
    ) === 0
      ? 'no'
      : 'yes';
  clientFormat[CLIENT_CAMPAIGN.NOTE] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.NOTE
  );
  clientFormat[CLIENT_CAMPAIGN.SHIPPING_OPTION] =
    parseInt(
      getFromTemplateData(templateDataJson, SERVER_CAMPAIGN.SHIPPING_OPTION),
      10
    ) === 0
      ? 'yes'
      : 'no';

  clientFormat[CLIENT_CAMPAIGN.PREVIEW_TYPE] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.PREVIEW_TYPE
  );
  const amountArrayFromServer = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.AMOUNT
  );
  if (amountArrayFromServer.length === 3) {
    clientFormat[CLIENT_CAMPAIGN.AMOUNT_TYPE] = 'merchant_recommended';
    clientFormat[CLIENT_CAMPAIGN.MERCHANT_AMOUNT_1] = formatNumber(
      amountArrayFromServer[0].value || 0
    );
    clientFormat[CLIENT_CAMPAIGN.MERCHANT_AMOUNT_2] = formatNumber(
      amountArrayFromServer[1].value || 0
    );
    clientFormat[CLIENT_CAMPAIGN.MERCHANT_AMOUNT_3] = formatNumber(
      amountArrayFromServer[2].value || 0
    );
  } else if (amountArrayFromServer.length === 1) {
    clientFormat[CLIENT_CAMPAIGN.AMOUNT] = formatNumber(
      amountArrayFromServer[0].value || 0
    );
    if (amountArrayFromServer[0].value !== 0) {
      clientFormat[CLIENT_CAMPAIGN.AMOUNT_TYPE] = 'fixed';
    } else {
      clientFormat[CLIENT_CAMPAIGN.AMOUNT_TYPE] = 'variable';
    }
  }

  const button_image_url = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.BUTTON_IMAGE_URL
  );
  if (!_isEmpty(button_image_url)) {
    clientFormat[CLIENT_CAMPAIGN.NO_PAYPAL_BUTTON_STYLES] = 'no';
    clientFormat[CLIENT_CAMPAIGN.BUTTON_IMAGE_URL] = button_image_url;
    clientFormat[CLIENT_CAMPAIGN.USE_PAYPAL_BUTTON_STYLE] =
      shouldUsePayPalButtonStyle(button_image_url);
    clientFormat[CLIENT_CAMPAIGN.SHOW_ACCEPTED_CARD_LOGOS] =
      shouldShowAcceptedCardLogos(button_image_url);
  } else {
    clientFormat[CLIENT_CAMPAIGN.USE_PAYPAL_BUTTON_STYLE] = 'yes';
    clientFormat[CLIENT_CAMPAIGN.NO_PAYPAL_BUTTON_STYLES] = 'yes';
  }
  clientFormat[CLIENT_CAMPAIGN.BTN_SIZE] = getButtonSize(button_image_url);
  clientFormat[CLIENT_CAMPAIGN.LANGUAGE] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.LANGUAGE
  );
  clientFormat[CLIENT_CAMPAIGN.CANCEL_URL] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.CANCEL_URL
  );
  clientFormat[CLIENT_CAMPAIGN.RETURN_URL] = getFromTemplateData(
    templateDataJson,
    SERVER_CAMPAIGN.RETURN_URL
  );
  clientFormat[CLIENT_CAMPAIGN.NO_CANCEL_RETURN] =
    !_isEmpty(clientFormat[CLIENT_CAMPAIGN.CANCEL_URL]) ||
    !_isEmpty(clientFormat[CLIENT_CAMPAIGN.RETURN_URL])
      ? 'no'
      : 'yes';

  clientFormat[CLIENT_CAMPAIGN.NO_EMBED_WIDGET] = clientFormat[
    CLIENT_CAMPAIGN.EMBED_WIDGET
  ]
    ? 'no'
    : 'yes';

  clientFormat[CLIENT_CAMPAIGN.METADATA] =
    serverFormat[SERVER_CAMPAIGN.METADATA];

  return clientFormat;
};

export const transformClientFormatToServer = (clientFormat) => {
  const serverFormat = {};

  if (clientFormat.id) {
    serverFormat[SERVER_CAMPAIGN.ID] = clientFormat[CLIENT_CAMPAIGN.ID];
  }

  serverFormat[SERVER_CAMPAIGN.CAMPAIGN_TYPE] =
    clientFormat[CLIENT_CAMPAIGN.CAMPAIGN_TYPE];
  serverFormat[SERVER_CAMPAIGN.CREATOR_ID] =
    clientFormat[CLIENT_CAMPAIGN.CREATOR_ID];
  serverFormat[SERVER_CAMPAIGN.STATUS] = clientFormat[CLIENT_CAMPAIGN.STATUS];
  serverFormat[SERVER_CAMPAIGN.EVENT_TYPE] =
    clientFormat[CLIENT_CAMPAIGN.EVENT_TYPE];
  serverFormat[SERVER_CAMPAIGN.EVENT_NAME] =
    clientFormat[CLIENT_CAMPAIGN.EVENT_NAME];
  serverFormat[SERVER_CAMPAIGN.EVENT_TITLE] =
    clientFormat[CLIENT_CAMPAIGN.EVENT_TITLE];
  serverFormat[SERVER_CAMPAIGN.EVENT_URL] =
    clientFormat[CLIENT_CAMPAIGN.EVENT_URL];
  serverFormat[SERVER_CAMPAIGN.EVENT_DESCRIPTION] =
    clientFormat[CLIENT_CAMPAIGN.EVENT_DESCRIPTION];
  serverFormat[SERVER_CAMPAIGN.EVENT_DESCRIPTION_RICH_TEXT] =
    clientFormat[CLIENT_CAMPAIGN.EVENT_DESCRIPTION_RICH_TEXT] &&
    JSON.stringify(clientFormat[CLIENT_CAMPAIGN.EVENT_DESCRIPTION_RICH_TEXT]);
  serverFormat[SERVER_CAMPAIGN.COUNTRY_CODE] =
    clientFormat[CLIENT_CAMPAIGN.COUNTRY_CODE];
  serverFormat[SERVER_CAMPAIGN.CURRENCY_CODE] =
    clientFormat[CLIENT_CAMPAIGN.CURRENCY_CODE];
  serverFormat[SERVER_CAMPAIGN.CREATOR_ID] =
    clientFormat[CLIENT_CAMPAIGN.CREATOR_ID];
  serverFormat[SERVER_CAMPAIGN.PARTICIPANTS] = [];
  serverFormat[SERVER_CAMPAIGN.CATEGORIES] = [
    ...clientFormat[CLIENT_CAMPAIGN.CATEGORIES],
  ];
  serverFormat[SERVER_CAMPAIGN.POSTAL_CODE] =
    clientFormat[CLIENT_CAMPAIGN.POSTAL_CODE];
  serverFormat[SERVER_CAMPAIGN.START_TIME] =
    clientFormat[CLIENT_CAMPAIGN.START_TIME];

  if (clientFormat[CLIENT_CAMPAIGN.HAS_END_TIME] === 'yes') {
    const eodTime = getEndOfDayTime(clientFormat[CLIENT_CAMPAIGN.END_TIME]);

    serverFormat[SERVER_CAMPAIGN.END_TIME] = clientFormat[
      CLIENT_CAMPAIGN.END_TIME
    ]
      ? eodTime
      : 0;
  } else {
    serverFormat[SERVER_CAMPAIGN.END_TIME] = 0;
  }

  serverFormat[SERVER_CAMPAIGN.TARGET_AMOUNT] =
    clientFormat[CLIENT_CAMPAIGN.TARGET_AMOUNT] !== '' &&
    normalizeNumber(clientFormat[CLIENT_CAMPAIGN.TARGET_AMOUNT]) !== 0
      ? normalizeNumber(clientFormat[CLIENT_CAMPAIGN.TARGET_AMOUNT])
      : null;

  // template_data variables
  serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA] = {};

  if (clientFormat[CLIENT_CAMPAIGN.NO_PAYPAL_BUTTON_STYLES] === 'no') {
    if (clientFormat[CLIENT_CAMPAIGN.USE_PAYPAL_BUTTON_STYLE] === 'no') {
      addToTemplateData(
        clientFormat,
        CLIENT_CAMPAIGN.BUTTON_IMAGE_URL,
        serverFormat,
        SERVER_CAMPAIGN.BUTTON_IMAGE_URL
      );
    } else {
      const buttonImageProps = getButtonImageAndUrl(
        clientFormat[CLIENT_CAMPAIGN.BUTTON_SIZE],
        clientFormat[CLIENT_CAMPAIGN.SHOW_ACCEPTED_CARD_LOGOS],
        clientFormat[CLIENT_CAMPAIGN.LANGUAGE],
        clientFormat[CLIENT_CAMPAIGN.COUNTRY_CODE]
      );
      addToTemplateData(
        buttonImageProps,
        CLIENT_CAMPAIGN.BUTTON_IMAGE_URL,
        serverFormat,
        SERVER_CAMPAIGN.BUTTON_IMAGE_URL
      );
      addToTemplateData(
        buttonImageProps,
        CLIENT_CAMPAIGN.BUTTON_IMAGE,
        serverFormat,
        SERVER_CAMPAIGN.BUTTON_IMAGE
      );
    }
  }
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.LANGUAGE,
    serverFormat,
    SERVER_CAMPAIGN.LANGUAGE
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.BANNER_URL,
    serverFormat,
    SERVER_CAMPAIGN.BANNER_URL
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.LOGO_URL,
    serverFormat,
    SERVER_CAMPAIGN.LOGO_URL
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.LOGO_TARGET,
    serverFormat,
    SERVER_CAMPAIGN.LOGO_TARGET
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.RECURRING_SELECTION,
    serverFormat,
    SERVER_CAMPAIGN.RECURRING_SELECTION
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.ANONYMOUS_SELECTION,
    serverFormat,
    SERVER_CAMPAIGN.ANONYMOUS_SELECTION
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.COVER_FEES,
    serverFormat,
    SERVER_CAMPAIGN.COVER_FEES
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.NOTE,
    serverFormat,
    SERVER_CAMPAIGN.NOTE
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.SHIPPING_OPTION,
    serverFormat,
    SERVER_CAMPAIGN.SHIPPING_OPTION
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.PREVIEW_TYPE,
    serverFormat,
    SERVER_CAMPAIGN.PREVIEW_TYPE
  );
  const noEmbedWidget = clientFormat[CLIENT_CAMPAIGN.NO_EMBED_WIDGET] === 'yes';

  if (!noEmbedWidget) {
    addToTemplateData(
      clientFormat,
      CLIENT_CAMPAIGN.EMBED_WIDGET,
      serverFormat,
      SERVER_CAMPAIGN.EMBED_WIDGET
    );
  }

  const noCancelReturn =
    clientFormat[CLIENT_CAMPAIGN.NO_CANCEL_RETURN] === 'yes';
  if (!noCancelReturn) {
    addToTemplateData(
      clientFormat,
      CLIENT_CAMPAIGN.CANCEL_URL,
      serverFormat,
      SERVER_CAMPAIGN.CANCEL_URL
    );
    addToTemplateData(
      clientFormat,
      CLIENT_CAMPAIGN.RETURN_URL,
      serverFormat,
      SERVER_CAMPAIGN.RETURN_URL
    );
  }

  // Amount field
  const amountType = clientFormat[CLIENT_CAMPAIGN.AMOUNT_TYPE];
  serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][SERVER_CAMPAIGN.DONATION_UNITS] =
    {};
  let amountArray = [];
  if (
    amountType === 'fixed' &&
    clientFormat[CLIENT_CAMPAIGN.AMOUNT] !== undefined
  ) {
    amountArray = [
      {
        [SERVER_CAMPAIGN.VALUE]: normalizeNumber(
          clientFormat[CLIENT_CAMPAIGN.AMOUNT] || 0
        ),
        [SERVER_CAMPAIGN.CURRENCY]: clientFormat[CLIENT_CAMPAIGN.CURRENCY_CODE],
      },
    ];
  } else if (amountType === 'variable') {
    amountArray = [
      {
        [SERVER_CAMPAIGN.VALUE]: 0,
        [SERVER_CAMPAIGN.CURRENCY]: clientFormat[CLIENT_CAMPAIGN.CURRENCY_CODE],
      },
    ];
  } else if (amountType === 'merchant_recommended') {
    amountArray = [
      {
        [SERVER_CAMPAIGN.VALUE]: normalizeNumber(
          clientFormat[CLIENT_CAMPAIGN.MERCHANT_AMOUNT_1]
        ),
        [SERVER_CAMPAIGN.CURRENCY]: clientFormat[CLIENT_CAMPAIGN.CURRENCY_CODE],
      },
      {
        [SERVER_CAMPAIGN.VALUE]: normalizeNumber(
          clientFormat[CLIENT_CAMPAIGN.MERCHANT_AMOUNT_2]
        ),
        [SERVER_CAMPAIGN.CURRENCY]: clientFormat[CLIENT_CAMPAIGN.CURRENCY_CODE],
      },
      {
        [SERVER_CAMPAIGN.VALUE]: normalizeNumber(
          clientFormat[CLIENT_CAMPAIGN.MERCHANT_AMOUNT_3]
        ),
        [SERVER_CAMPAIGN.CURRENCY]: clientFormat[CLIENT_CAMPAIGN.CURRENCY_CODE],
      },
    ];
  }
  serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][SERVER_CAMPAIGN.DONATION_UNITS] =
    {
      [SERVER_CAMPAIGN.AMOUNT]: amountArray,
    };
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.AMOUNT,
    serverFormat,
    SERVER_CAMPAIGN.AMOUNT
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.MERCHANT_AMOUNT_1,
    serverFormat,
    SERVER_CAMPAIGN.MERCHANT_AMOUNT_1
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.MERCHANT_AMOUNT_2,
    serverFormat,
    SERVER_CAMPAIGN.MERCHANT_AMOUNT_2
  );
  addToTemplateData(
    clientFormat,
    CLIENT_CAMPAIGN.MERCHANT_AMOUNT_3,
    serverFormat,
    SERVER_CAMPAIGN.MERCHANT_AMOUNT_3
  );

  if (clientFormat.status === 'DRAFT') {
    serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA][SERVER_CAMPAIGN.DRAFT_META] = {
      [SERVER_CAMPAIGN.DRAFT_VERSION]:
        clientFormat[CLIENT_CAMPAIGN.DRAFT_META][CLIENT_CAMPAIGN.DRAFT_VERSION],
      [SERVER_CAMPAIGN.DRAFT_STEP]:
        clientFormat[CLIENT_CAMPAIGN.DRAFT_META][CLIENT_CAMPAIGN.DRAFT_STEP],
    };
  }

  const templateDataString = JSON.stringify(
    serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA]
  );

  serverFormat[SERVER_CAMPAIGN.TEMPLATE_DATA] = templateDataString;

  return serverFormat;
};
